<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { getCurrentRelease } from '@/api/system'
import type { SystemRelease } from '@/api/system'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const currentRelease = ref<SystemRelease | null>(null)
const fullPage = computed(() => ['/login', '/change-password', '/forbidden', '/legal/privacy', '/legal/usage', '/support/contact'].includes(route.path))
const releaseVersion = computed(() => currentRelease.value?.release_version || 'local')
const releaseTooltipId = 'system-release-tooltip'

const fallbackText = (value?: string | null) => {
  const text = value?.trim()
  return text || '-'
}

const formatReleaseTime = (value?: string) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const pad = (number: number) => String(number).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const releaseDetailRows = computed(() => {
  const release = currentRelease.value
  return [
    { label: '环境', value: fallbackText(release?.environment) },
    { label: '后端镜像', value: fallbackText(release?.backend_image) },
    { label: '前端镜像', value: fallbackText(release?.frontend_image) },
    { label: 'Git Commit', value: fallbackText(release?.git_commit) },
    { label: '发布人', value: fallbackText(release?.created_by) },
    { label: '发布时间', value: formatReleaseTime(release?.created_at) },
    { label: '发布说明', value: fallbackText(release?.release_notes) },
  ]
})

const activeSection = computed(() => {
  if (route.path.startsWith('/iam')) return 'iam'
  if (route.path.startsWith('/strategy-service')) return 'strategy-service'
  if (route.path.startsWith('/data-service/sources') || route.path.startsWith('/data-service/catalog') || route.path.startsWith('/data-service/datasets')) return 'sources'
  if (route.path.startsWith('/data-service/products')) return 'products'
  if (route.path.startsWith('/data-service/operations')) return 'operations'
  return 'dashboard'
})

const isDataServiceActive = computed(() => ['sources', 'products', 'operations'].includes(activeSection.value))

type NavLeafItem = {
  key: string
  label: string
  path: string
  icon: string
  permission?: string
  anyPermission?: string[]
}

type NavGroupItem = {
  key: string
  label: string
  icon: string
  children: NavLeafItem[]
}

type NavItem = NavLeafItem | NavGroupItem

const hasChildren = (item: NavItem): item is NavGroupItem => 'children' in item

const navItems: NavItem[] = [
  {
    key: 'dashboard',
    label: '运营总览',
    path: '/data-service',
    permission: 'dashboard:read',
    icon: `<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>`,
  },
  {
    key: 'data-service',
    label: '数据服务',
    icon: `<path d="M4 6a8 3 0 1 0 16 0A8 3 0 1 0 4 6"/><path d="M4 6v6a8 3 0 1 0 16 0V6"/><path d="M4 12v6a8 3 0 1 0 16 0v-6"/>`,
    children: [
      {
        key: 'sources',
        label: '数据来源',
        path: '/data-service/sources',
        anyPermission: ['datasource:read', 'dataset:read'],
        icon: `<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>`,
      },
      {
        key: 'products',
        label: '数据产品',
        path: '/data-service/products',
        permission: 'product:read',
        icon: `<rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>`,
      },
      {
        key: 'operations',
        label: '同步策略',
        path: '/data-service/operations',
        permission: 'operations:read',
        icon: `<circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>`,
      },
    ],
  },
  {
    key: 'strategy-service',
    label: '策略服务',
    path: '/strategy-service',
    icon: `<path d="M4 19V5"/><path d="M4 19h16"/><path d="M8 15l3-3 3 2 5-7"/><path d="M17 7h2v2"/>`,
  },
  {
    key: 'iam',
    label: '身份权限',
    path: '/iam',
    anyPermission: ['iam:user:manage', 'iam:role:manage', 'iam:token:manage'],
    icon: `<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/>`,
  },
]

const canViewNavItem = (item: NavItem) => {
  if (hasChildren(item)) {
    return item.children.some((child) =>
      child.anyPermission ? auth.hasAnyPermission(child.anyPermission) : Boolean(child.permission && auth.hasPermission(child.permission))
    )
  }
  if (item.anyPermission) return auth.hasAnyPermission(item.anyPermission)
  return item.permission ? auth.hasPermission(item.permission) : true
}

const visibleNavItems = computed(() => navItems.filter(canViewNavItem))

const visibleChildren = (item: NavItem) => {
  if (!hasChildren(item)) return []
  return item.children.filter((child) =>
    child.anyPermission ? auth.hasAnyPermission(child.anyPermission) : Boolean(child.permission && auth.hasPermission(child.permission))
  )
}

const dataServiceNavItem = computed(() => visibleNavItems.value.find((item) => item.key === 'data-service'))
const dataServiceChildren = computed(() => dataServiceNavItem.value ? visibleChildren(dataServiceNavItem.value) : [])
const showDataServiceSubnav = computed(() => isDataServiceActive.value && dataServiceChildren.value.length > 0)

const isNavItemActive = (item: NavItem) => {
  if (hasChildren(item)) {
    return item.key === 'data-service' && isDataServiceActive.value
  }
  return activeSection.value === item.key
}

const navigateNavItem = (item: NavItem) => {
  if (hasChildren(item)) {
    const firstChild = visibleChildren(item)[0]
    if (firstChild) router.push(firstChild.path)
    return
  }
  router.push(item.path)
}

const logout = async () => {
  await auth.logout()
  currentRelease.value = null
  await router.replace('/login')
}

const loadCurrentRelease = async () => {
  if (!auth.isAuthenticated) {
    currentRelease.value = null
    return
  }
  try {
    currentRelease.value = await getCurrentRelease()
  } catch {
    currentRelease.value = null
  }
}

watch(
  () => [auth.initialized, auth.isAuthenticated] as const,
  ([initialized, isAuthenticated]) => {
    if (initialized && isAuthenticated) {
      loadCurrentRelease()
    } else if (initialized) {
      currentRelease.value = null
    }
  },
  { immediate: true }
)
</script>

<template>
  <RouterView v-if="fullPage" />
  <div v-else-if="!auth.initialized" class="app-boot">
    <div class="boot-panel">
      <div class="boot-mark">
        <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
          <circle cx="10" cy="10" r="7" stroke="white" stroke-width="2"/>
          <circle cx="10" cy="10" r="3" fill="white"/>
        </svg>
      </div>
      <div class="boot-copy">
        <strong>正在恢复登录状态</strong>
        <span>请稍候</span>
      </div>
    </div>
  </div>
  <div v-else class="app-shell">
    <header class="app-header">
      <div class="app-topbar">
        <div class="brand">
          <div class="logo-mark">
            <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
              <circle cx="10" cy="10" r="7" stroke="white" stroke-width="2"/>
              <circle cx="10" cy="10" r="3" fill="white"/>
            </svg>
          </div>
          <span class="logo-text">Orion</span>
        </div>

        <nav class="primary-nav" aria-label="主导航">
          <button
            v-for="item in visibleNavItems"
            :key="item.key"
            class="nav-item"
            :class="{ active: isNavItemActive(item) }"
            type="button"
            @click="navigateNavItem(item)"
          >
            <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" v-html="item.icon" />
            <span class="nav-label">{{ item.label }}</span>
          </button>
        </nav>

        <div class="user-actions">
          <div class="release-info">
            <button
              class="release-version"
              type="button"
              :aria-describedby="releaseTooltipId"
              :aria-label="`系统版本 v${releaseVersion}`"
            >
              v{{ releaseVersion }}
            </button>
            <div
              :id="releaseTooltipId"
              class="release-tooltip"
              role="tooltip"
              aria-label="系统版本详情"
            >
              <div class="release-tooltip-title">系统版本详情</div>
              <dl class="release-detail-list">
                <template v-for="row in releaseDetailRows" :key="row.label">
                  <dt>{{ row.label }}</dt>
                  <dd>{{ row.value }}</dd>
                </template>
              </dl>
            </div>
          </div>
          <details class="user-menu">
            <summary class="user-trigger" :title="auth.displayName">
              <span class="user-name">{{ auth.displayName }}</span>
              <svg class="user-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="m6 9 6 6 6-6"/>
              </svg>
            </summary>
            <div class="user-menu-panel">
              <button type="button" @click="router.push('/change-password')">修改密码</button>
              <button type="button" @click="logout">退出登录</button>
            </div>
          </details>
        </div>
      </div>
    </header>

    <nav v-if="showDataServiceSubnav" class="app-subnav" aria-label="数据服务导航">
      <button
        v-for="child in dataServiceChildren"
        :key="child.key"
        class="subnav-item"
        :class="{ active: activeSection === child.key }"
        type="button"
        @click="router.push(child.path)"
      >
        <svg class="nav-icon subnav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" v-html="child.icon" />
        <span>{{ child.label }}</span>
      </button>
    </nav>

    <main class="app-main">
      <RouterView />
    </main>
  </div>
</template>

<style scoped>
.app-shell {
  display: flex;
  width: 100%;
  min-height: 100vh;
  flex-direction: column;
  background: var(--color-bg);
}

.app-boot {
  display: grid;
  min-height: 100vh;
  place-items: center;
  background: var(--color-bg);
}

.boot-panel {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-sm);
}

.boot-mark {
  display: grid;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  place-items: center;
  border-radius: 8px;
  background: linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%);
}

.boot-copy {
  display: grid;
  gap: 2px;
}

.boot-copy strong {
  color: var(--color-text-primary);
  font-size: 14px;
  font-weight: 700;
}

.boot-copy span {
  color: var(--color-text-secondary);
  font-size: 12.5px;
}

.app-header {
  position: sticky;
  z-index: 30;
  top: 0;
  flex-shrink: 0;
  border-bottom: 1px solid rgb(15 23 42 / 0.18);
  background: var(--color-nav-bg);
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.08);
}

.app-topbar {
  display: flex;
  min-height: 52px;
  align-items: center;
  gap: 18px;
  padding: 0 28px;
}

.brand {
  display: inline-flex;
  min-width: 118px;
  flex-shrink: 0;
  align-items: center;
  gap: 9px;
}

.logo-mark {
  display: flex;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  background: linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%);
}

.logo-text {
  color: var(--color-text-inverse);
  font-size: 15.5px;
  font-weight: 650;
  letter-spacing: 0;
  white-space: nowrap;
}

.primary-nav {
  display: flex;
  min-width: 0;
  height: 52px;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  scrollbar-width: none;
}

.primary-nav {
  flex: 1;
}

.primary-nav::-webkit-scrollbar,
.app-subnav::-webkit-scrollbar {
  display: none;
}

.nav-item,
.subnav-item {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 7px;
  border: none;
  background: transparent;
  color: var(--color-nav-text);
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 600;
  white-space: nowrap;
  transition: color var(--transition-fast), background var(--transition-fast);
}

.nav-item {
  height: 52px;
  padding: 0 11px;
  border-radius: 0;
}

.subnav-item {
  height: 32px;
  padding: 0 10px;
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  font-size: 13px;
}

.nav-item:hover {
  background: transparent;
  color: var(--color-nav-text-active);
}

.subnav-item:hover {
  background: var(--color-surface-raised);
  color: var(--color-text-primary);
}

.nav-item.active {
  background: transparent;
  color: var(--color-nav-text-active);
}

.nav-item.active::after {
  position: absolute;
  right: 11px;
  bottom: 0;
  left: 11px;
  height: 2px;
  border-radius: 999px 999px 0 0;
  background: var(--color-nav-accent);
  content: "";
}

.subnav-item.active {
  background: var(--color-brand-light);
  color: var(--color-brand);
}

.nav-icon {
  width: 17px;
  height: 17px;
  flex-shrink: 0;
  opacity: 0.86;
}

.subnav-icon {
  width: 16px;
  height: 16px;
  opacity: 0.82;
}

.nav-item.active .nav-icon,
.subnav-item.active .nav-icon {
  color: var(--color-nav-accent);
  opacity: 1;
}

.nav-label {
  min-width: 0;
}

.user-actions {
  display: inline-flex;
  max-width: 40vw;
  flex-shrink: 0;
  align-items: center;
  gap: 10px;
}

.release-version,
.user-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.release-info {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}

.release-version {
  max-width: 120px;
  padding: 4px 0;
  border: 0;
  background: transparent;
  color: var(--color-nav-text);
  cursor: default;
  font-size: 11.5px;
  font-weight: 500;
  opacity: 0.62;
}

.release-version:hover,
.release-version:focus-visible {
  color: var(--color-nav-text-active);
  opacity: 1;
  outline: none;
}

.release-tooltip {
  position: absolute;
  z-index: 50;
  top: calc(100% + 12px);
  right: 0;
  display: grid;
  width: min(420px, 80vw);
  max-width: 420px;
  gap: 10px;
  padding: 12px;
  border: 1px solid rgb(148 163 184 / 0.32);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
  opacity: 0;
  pointer-events: none;
  transform: translateY(-3px);
  transition: opacity var(--transition-fast), transform var(--transition-fast), visibility var(--transition-fast);
  visibility: hidden;
}

.release-info:hover .release-tooltip,
.release-info:focus-within .release-tooltip {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
  visibility: visible;
}

.release-tooltip::before {
  position: absolute;
  right: 18px;
  bottom: 100%;
  width: 10px;
  height: 10px;
  border-top: 1px solid rgb(148 163 184 / 0.32);
  border-left: 1px solid rgb(148 163 184 / 0.32);
  background: var(--color-surface);
  content: "";
  transform: translateY(5px) rotate(45deg);
}

.release-tooltip-title {
  color: var(--color-text-primary);
  font-size: 13px;
  font-weight: 700;
}

.release-detail-list {
  display: grid;
  grid-template-columns: 68px minmax(0, 1fr);
  gap: 7px 10px;
  margin: 0;
}

.release-detail-list dt,
.release-detail-list dd {
  min-width: 0;
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
}

.release-detail-list dt {
  color: var(--color-text-tertiary);
  font-weight: 600;
  white-space: nowrap;
}

.release-detail-list dd {
  overflow-wrap: anywhere;
  color: var(--color-text-secondary);
}

.user-menu {
  position: relative;
  flex-shrink: 0;
}

.user-menu summary {
  list-style: none;
}

.user-menu summary::-webkit-details-marker {
  display: none;
}

.user-trigger {
  display: inline-flex;
  height: 30px;
  max-width: 180px;
  align-items: center;
  gap: 5px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-nav-text-active);
  cursor: pointer;
  padding: 0 7px 0 9px;
}

.user-name {
  max-width: 142px;
  color: inherit;
  font-size: 12.5px;
  font-weight: 600;
}

.user-chevron {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: var(--color-nav-text);
}

.user-trigger:hover {
  background: var(--color-nav-bg-hover);
  color: var(--color-nav-text-active);
}

.user-menu[open] .user-chevron {
  transform: rotate(180deg);
}

.user-menu-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  display: grid;
  min-width: 128px;
  padding: 6px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}

.user-menu-panel button {
  height: 32px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  text-align: left;
  padding: 0 9px;
}

.user-menu-panel button:hover {
  background: var(--color-surface-raised);
  color: var(--color-text-primary);
}

.app-subnav {
  display: flex;
  min-height: 48px;
  flex-shrink: 0;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  padding: 0 var(--space-8);
  border-bottom: 1px solid var(--color-border);
  background: rgb(248 250 252 / 0.72);
  scrollbar-width: none;
}

.app-main {
  flex: 1;
  min-width: 0;
  padding: var(--space-8);
  overflow: auto;
}

@media (max-width: 900px) {
  .app-topbar {
    gap: 12px;
    padding: 0 16px;
  }

  .brand {
    min-width: auto;
  }

  .logo-text {
    display: none;
  }

  .user-actions {
    max-width: 34vw;
  }

  .release-version {
    display: none;
  }

  .release-info {
    display: none;
  }

  .app-subnav {
    padding-right: 16px;
    padding-left: 16px;
  }
}
</style>
